import { notification } from 'antd';
import { API_HOST, ENV, GMAIL_CONNECT_URL, OAUTH2_TOKEN_URL } from '../constants/apiUrls';
import configData from '../config/loader';
import { getCsrfToken } from './csrf';
import { getWorkspaceFromUrl } from './workspace';
import {
  QUOTA_EXCEEDED,
  MAINTENANCE_MODE,
  PASSWORD_RESET_REQUIRED,
} from '../constants/platformStatusCodes';
import {
  SET_QUOTA_EXCEEDED_INFO,
  SET_MAINTENANCE_MODE,
  SET_PASSWORD_RESET_REQUIRED,
  CLEAR_PASSWORD_RESET_REQUIRED,
} from '../constants/actionTypes';

// Request configuration constants
const DEFAULT_TIMEOUT_MS = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000; // 1 second base delay for exponential backoff
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

// POSTs with side effects are never auto-retried: a gateway error or dropped
// connection doesn't prove the backend didn't already act (e.g. queue the email),
// so a retry could send a duplicate. Auth codes are single-use, so a repeated
// /gmail/connect can only fail or create a second connection.
const NON_RETRYABLE_ENDPOINTS = new Set([
  '/outreach/sendEmail',
  '/outreach/massOutreach',
  GMAIL_CONNECT_URL,
]);

const getMaxRetries = (endPoint, options = {}) =>
  options.retry === false || NON_RETRYABLE_ENDPOINTS.has(endPoint) ? 0 : MAX_RETRIES;

function resolveWorkspaceSlugForApi(state) {
  if (typeof window !== 'undefined') {
    try {
      const fromUrl = getWorkspaceFromUrl();
      if (fromUrl) return fromUrl;
    } catch {
      
    }
  }
  return state?.login?.session?.workspace?.name ?? null;
}

function buildTenantWorkspaceHeaders(state) {
  if (!state) return {};
  const headers = {};
  if (configData?.companyName) {
    headers['X-Tenant'] = configData.tenantName;
  }
  const workspace = resolveWorkspaceSlugForApi(state);
  if (workspace) {
    headers['X-Workspace'] = workspace;
  }
  return headers;
}

function isForbiddenApiErrorPayload(data) {
  if (!data) return false;
  if (data.password_reset_required || data.error === PASSWORD_RESET_REQUIRED) return false;
  return Number(data.httpStatus) === 403;
}

function notifyForbiddenAction(data) {
  const description =
    data?.message ||
    data?.error ||
    (typeof data?.errors === 'object' && data.errors
      ? Object.values(data.errors)
          .flat()
          .find(Boolean)
      : null) ||
    'You do not have permission to perform this action.';

  notification.error({
    title: '',
    description,
    duration: 8,
    placement: 'topRight',
  });
}

function arClient() {
  const client = {};

  // Track active requests for cancellation
  const activeRequests = new Map();
  let formDataRequestSeq = 0;

  // Lazy getter for store to avoid circular dependency
  let storeInstance = null;
  const getStore = () => {
    if (!storeInstance) {
      // eslint-disable-next-line global-require
      storeInstance = require('../store').default;
    }
    return storeInstance;
  };

  const showAlert = (errorMsg, type = 'error') => {
    if (ENV !== 'PRODUCTION') {
      notification[type]({
        title: 'API Failed!!',
        duration: 5,
        maxCount: 2,
        description: errorMsg,
        onClick: () => {},
      });
    }
  };

  // Service provider configuration - only OAuth 2.0 endpoints
  const consumerConfiguration = {
    serviceProvider: {
      host: API_HOST,
      // Active endpoints (OAuth 2.0)
      getAccountData: '/account/data',
      addDictionaryURL: '/dictionary/add',
      analyzeText: '/analyze-text/master',
      analyzeTextV2: '/text-analyze-v2/taskListParagraph',
      flagWord: '/text-feedback/replaceWord',
    },
  };

  // OAuth 2.0 Bearer token support
  let bearerToken = null;
  let refreshToken = null;
  let tokenExpiry = null;
  let useOAuth2 = false;

  client.sophisticationBandIdsArray = {
    Genius: 1,
    Academic: 2,
    Specialist: 3,
    Knowledgeable: 4,
    General: 5,
  };

  /**
   * Initialize with OAuth 2.0 Bearer token
   * This is the new recommended method - no OAuth 1.0 flow needed
   *
   * @param {object} tokens - { access_token, refresh_token, expires_in }
   * @param {function} onSuccessCallback - Called when ready
   */
  client.initOAuth2 = function(tokens, onSuccessCallback) {
    useOAuth2 = true;
    bearerToken = tokens.access_token;
    refreshToken = tokens.refresh_token;
    // Use expires_at if available (page reload), otherwise calculate from expires_in
    tokenExpiry = tokens.expires_at || Date.now() + tokens.expires_in * 1000;

    // Store account info
    consumerConfiguration.consumerKey = tokens.access_token;
    consumerConfiguration.consumerSecret = tokens.account_id;

    // If token is already expired or about to expire, refresh immediately
    if (tokenExpiry && Date.now() > tokenExpiry - 60000) {
      refreshOAuth2Token();
    } else {
      // Schedule token refresh before expiry
      scheduleOAuth2TokenRefresh();
    }

    // Call success callback immediately (no OAuth 1.0 flow needed)
    if (typeof onSuccessCallback === 'function') {
      onSuccessCallback();
    }
  };

  /**
   * Schedule automatic token refresh before expiry
   */
  function scheduleOAuth2TokenRefresh() {
    if (!tokenExpiry || !useOAuth2) return;

    const refreshTime = tokenExpiry - Date.now() - 60000; // 1 minute before expiry
    if (refreshTime > 0) {
      setTimeout(() => {
        refreshOAuth2Token();
      }, refreshTime);
    }
  }

  /**
   * Refresh the OAuth 2.0 access token
   * Returns a Promise so callers (like 401 retry) can await the result
   */
  let refreshPromise = null;

  function refreshOAuth2Token() {
    // Avoid multiple simultaneous refresh requests
    if (refreshPromise) {
      return refreshPromise;
    }

    if (!refreshToken) {
      console.warn('No refresh token available');
      return Promise.reject(new Error('No refresh token available'));
    }

    // Access + refresh tokens are issued/refreshed via POST /oauth2/token (password + refresh_token grants).
    const refreshUrl = OAUTH2_TOKEN_URL;

    // Convert form data to URL-encoded format
    const formData = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    });

    refreshPromise = fetch(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        // The backend resolves the tenant from this header; login sends it, so the refresh must too,
        // otherwise /oauth2/token 400s with "Unable to resolve tenant" and the session drops.
        'X-Tenant': configData.tenantName,
      },
      body: formData,
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((result) => {
        if (result.access_token) {
          bearerToken = result.access_token;
          refreshToken = result.refresh_token;
          tokenExpiry = Date.now() + result.expires_in * 1000;
          scheduleOAuth2TokenRefresh();

          // Persist refreshed tokens to localStorage so page reload uses fresh values
          try {
            const stored = localStorage.getItem('session');
            if (stored) {
              const session = JSON.parse(stored);
              session.access_token = result.access_token;
              session.refresh_token = result.refresh_token;
              session.expires_in = result.expires_in;
              session.expires_at = tokenExpiry;
              localStorage.setItem('session', JSON.stringify(session));
            }
          } catch (e) {
            // localStorage may be unavailable
          }

          return bearerToken;
        }
        throw new Error('No access_token in refresh response');
      })
      .catch((error) => {
        console.error('OAuth 2.0 token refresh failed', error);
        // Clear tokens - user will need to re-login
        bearerToken = null;
        refreshToken = null;
        tokenExpiry = null;
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });

    return refreshPromise;
  }

  /**
   * Clear OAuth 2.0 tokens (logout)
   */
  client.clearOAuth2 = function() {
    useOAuth2 = false;
    bearerToken = null;
    refreshToken = null;
    tokenExpiry = null;
  };

  /**
   * Check if using OAuth 2.0 mode
   */
  client.isOAuth2 = function() {
    return useOAuth2;
  };

  /**
   * Legacy init() method - now a no-op for OAuth 2.0
   * OAuth 2.0 tokens are managed by Redux state, so init is not needed
   * This stub exists for backward compatibility with 100+ existing calls
   */
  client.init = function(
    apiHost,
    consumerKey,
    consumerSecret,
    onSuccessCallbackFunc,
    onErrorCallbackFunc,
    username,
    password
  ) {
    // Silent no-op - OAuth 2.0 token is managed by Redux state
    // Call success callback immediately
    if (typeof onSuccessCallbackFunc === 'function') {
      onSuccessCallbackFunc();
    }
    return this;
  };

  /**
   * Legacy initV2() method - now a no-op for OAuth 2.0
   */
  client.initV2 = function(
    consumerKey,
    consumerSecret,
    onSuccessCallbackFunc,
    onErrorCallbackFunc
  ) {
    // Silent no-op - OAuth 2.0 token is managed by Redux state
    if (typeof onSuccessCallbackFunc === 'function') {
      onSuccessCallbackFunc();
    }
  };

  /**
   * Get account data - Converted to OAuth 2.0
   */
  client.getAccountData = function(callback) {
    return client.doApiCall(consumerConfiguration.serviceProvider.getAccountData, {}, callback);
  };

  /**
   * Add word to dictionary - Converted to OAuth 2.0
   */
  client.addDictionary = function(word, callback) {
    return client.doApiCall(
      consumerConfiguration.serviceProvider.addDictionaryURL,
      { word },
      callback
    );
  };

  /**
   * Analyze text using the master endpoint
   */
  client.analyzeText = function(requestData, callback) {
    if (typeof requestData.sophisticationBandId === 'undefined') {
      requestData.sophisticationBandId = client.sophisticationBandIdsArray.Knowledgeable;
    }

    if (typeof requestData.serviceNamesArray === 'object') {
      requestData.serviceNamesArray = JSON.stringify(requestData.serviceNamesArray);
    }

    return client.doApiCall(
      consumerConfiguration.serviceProvider.analyzeText,
      requestData,
      callback
    );
  };

  client.analyzeTextV2 = function(requestData, callback) {
    // if (typeof requestData.sophisticationBandId==='undefined')
    //   {requestData["sophisticationBandId"] = client.sophisticationBandIdsArray["Knowledgeable"];}

    // if (typeof requestData.serviceNamesArray === 'object')
    //   {requestData["serviceNamesArray"] = JSON.stringify(requestData["serviceNamesArray"]);}

    return client.doApiCall(
      consumerConfiguration.serviceProvider.analyzeTextV2,
      requestData,
      callback
    );
  };

  client.flagWord = function(requestData, callback) {
    return client.doApiCall(consumerConfiguration.serviceProvider.flagWord, requestData, callback);
  };

  /**
   * Create an AbortController with timeout
   * @param {number} timeoutMs - Timeout in milliseconds
   * @returns {AbortController}
   */
  function createTimeoutController(timeoutMs = DEFAULT_TIMEOUT_MS) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    // Store timeout ID for cleanup
    controller.timeoutId = timeoutId;
    return controller;
  }

  /**
   * Clean up AbortController timeout
   * @param {AbortController} controller
   */
  function cleanupController(controller) {
    if (controller && controller.timeoutId) {
      clearTimeout(controller.timeoutId);
    }
  }

  /**
   * Execute fetch with retry logic
   * @param {string} url - Request URL
   * @param {object} options - Fetch options
   * @param {number} retryCount - Current retry attempt
   * @param {AbortController} controller - AbortController for cancellation
   * @param {number} maxRetries - Retry budget (0 for non-idempotent calls, see getMaxRetries)
   * @returns {Promise<Response>}
   */
  async function fetchWithRetry(
    url,
    options,
    retryCount = 0,
    controller = null,
    maxRetries = MAX_RETRIES
  ) {
    const ctrl = controller || createTimeoutController();
    const fetchOptions = { ...options, signal: ctrl.signal };

    try {
      const response = await fetch(url, fetchOptions);

      // Check if response is retryable
      if (RETRYABLE_STATUS_CODES.includes(response.status) && retryCount < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchWithRetry(url, options, retryCount + 1, ctrl, maxRetries);
      }

      cleanupController(ctrl);
      return response;
    } catch (error) {
      // Retry on network errors (but not abort). Keep the same controller so the
      // retry stays cancellable and its timeout still applies.
      if (error.name !== 'AbortError' && retryCount < maxRetries) {
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchWithRetry(url, options, retryCount + 1, ctrl, maxRetries);
      }

      cleanupController(ctrl);

      throw error;
    }
  }

  /**
   * Cancel a pending request by endpoint
   * @param {string} endPoint - The endpoint to cancel
   */
  client.cancelRequest = function(endPoint) {
    const controller = activeRequests.get(endPoint);
    if (controller) {
      controller.abort();
      activeRequests.delete(endPoint);
    }
  };

  /**
   * Cancel all pending requests
   */
  client.cancelAllRequests = function() {
    activeRequests.forEach((controller) => {
      controller.abort();
    });
    activeRequests.clear();
  };

  client.doApiCall = function(endPoint, requestData, callback, doEncodeURIComponent, options = {}) {
    // Check for OAuth 2.0 token - try Redux state first, then local bearerToken
    const store = getStore();
    const state = store.getState();
    const accessToken = state?.login?.session?.access_token || bearerToken;

    if (!accessToken) {
      // Silent failure - user needs to login, not an error condition
      callback({
        status: 'ERROR',
        error: 'Authentication required',
        errorMsg: 'Please log in to continue.',
      });
      return;
    }

    // A FormData body (multipart file upload) is sent as-is; the browser sets the
    // Content-Type with its boundary.
    const isFormData = typeof FormData !== 'undefined' && requestData instanceof FormData;

    // Create unique request key including parameters (prevents cancelling different requests to same endpoint).
    // FormData can't be serialized, so each upload gets its own key and never cancels a concurrent one.
    const requestKey = isFormData
      ? `${endPoint}:formdata:${(formDataRequestSeq += 1)}`
      : `${endPoint}:${JSON.stringify(requestData || {})}`;

    // Cancel any existing request with the SAME endpoint AND parameters (prevents duplicate calls)
    if (activeRequests.has(requestKey)) {
      const existingController = activeRequests.get(requestKey);
      existingController.abort();
    }

    // Create AbortController with timeout
    const timeoutMs = options.timeout || DEFAULT_TIMEOUT_MS;
    const controller = createTimeoutController(timeoutMs);
    activeRequests.set(requestKey, controller);

    // Use OAuth 2.0 Bearer token
    const url = consumerConfiguration.serviceProvider.host + endPoint;
    const csrfToken = getCsrfToken();

    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/x-www-form-urlencoded' }),
      Authorization: `Bearer ${accessToken}`,
      ...buildTenantWorkspaceHeaders(state),
    };

    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken;
    }

    // Build form data without double-encoding
    let body;
    if (isFormData) {
      body = requestData;
    } else {
      const params = new URLSearchParams();
      for (const key in requestData) {
        if (requestData.hasOwnProperty(key)) {
          params.append(key, requestData[key]);
        }
      }
      body = params.toString();
    }

    const fetchOptions = {
      method: options.method || 'POST',
      headers,
      credentials: 'include',
      body,
    };

    // Use fetchWithRetry for automatic retry logic
    fetchWithRetry(url, fetchOptions, 0, controller, getMaxRetries(endPoint, options))
      .then((response) => {
        // Clean up tracking
        activeRequests.delete(requestKey);

        // Handle 401 - try to refresh token and retry once
        if (response.status === 401 && refreshToken && !options.isRetryAfterRefresh) {
          return refreshOAuth2Token()
            .then((newToken) => {
              // Retry the original request with the new token
              const retryHeaders = {
                ...headers,
                Authorization: `Bearer ${newToken}`,
              };
              const retryOptions = {
                method: options.method || 'POST',
                headers: retryHeaders,
                credentials: 'include',
                body,
              };
              return fetch(url, retryOptions);
            })
            .then((retryResponse) => {
              if (retryResponse.status === 503) {
                return retryResponse.json().then((data) => ({
                  ...data,
                  status: MAINTENANCE_MODE,
                  _httpStatus: 503,
                  httpStatus: 503,
                }));
              }
              return retryResponse.json().then((data) => ({
                ...data,
                httpStatus: retryResponse.status,
              }));
            })
            .catch(() => {
              // Refresh failed - return original 401 response as error
              return { status: 'ERROR', error: 'Authentication expired. Please log in again.' };
            });
        }

        // Handle 503 maintenance mode at HTTP level
        if (response.status === 503) {
          return response.json().then((data) => ({
            ...data,
            status: MAINTENANCE_MODE,
            _httpStatus: 503,
            httpStatus: 503,
          }));
        }
        return response.json().then((data) => ({
          ...data,
          httpStatus: response.status,
        }));
      })
      .then((data) => {
        // Handle maintenance mode (503 response)
        if (data.status === MAINTENANCE_MODE || data.status === 503) {
          store.dispatch({
            type: SET_MAINTENANCE_MODE,
            payload: {
              message: data.message,
              retry_after: data.retry_after,
              estimated_end: data.estimated_end,
            },
          });
          // Don't call the normal callback - let the app show maintenance page
          callback({ status: MAINTENANCE_MODE, ...data });
          return;
        }
        if (isForbiddenApiErrorPayload(data)) {
          notifyForbiddenAction(data);
          callback(data);
          return;
        }
        // Handle quota exceeded
        if (data.status === QUOTA_EXCEEDED) {
          store.dispatch({
            type: SET_QUOTA_EXCEEDED_INFO,
            quotaExceededInfo: {
              errorMessage: data.message,
            },
          });
        }
        // Handle password reset required (403 with password_reset_required flag)
        if (data.password_reset_required || data.error === PASSWORD_RESET_REQUIRED) {
          const currentState = store.getState();
          const alreadyFlagged = currentState?.login?.passwordResetRequired;

          store.dispatch({
            type: SET_PASSWORD_RESET_REQUIRED,
            payload: {
              message:
                data.error_description || 'Your password needs to be updated for security reasons.',
              resetUrl: data.reset_url || '/settings/profile',
            },
          });

          if (typeof window !== 'undefined') {
            const isOnSettingsPage =
              window.location.pathname.includes('/settings') &&
              window.location.search.includes('passwordReset=required');

            // Show notification once (when not already flagged)
            if (!alreadyFlagged) {
              notification.warning({
                title: 'Password Update Required',
                description: data.error_description || 'Please update your password to continue.',
                duration: 0, // Don't auto-close
              });
            }

            // Only redirect if not already on settings page
            if (!isOnSettingsPage) {
              window.location.href = '/settings/profile?passwordReset=required';
            }
          }
          callback({ status: 'ERROR', error: PASSWORD_RESET_REQUIRED, ...data });
          return;
        }
        // Normalize response - backend returns {status: 200, data: ...} or {status: 10, data: ...} or {status: 'OK', data: ...}
        // Normalize to 'OK' for consistency with platformStatusCodes.OK constant
        if (data.status === 200 || data.status === 10 || data.status === 'OK') {
          data.status = 'OK';
          // Clear password reset required state on successful password change
          if (endPoint.includes('change-password') || endPoint.includes('changePassword')) {
            store.dispatch({ type: CLEAR_PASSWORD_RESET_REQUIRED });
          }
        }
        callback(data);
      })
      .catch((error) => {
        // Clean up tracking
        activeRequests.delete(requestKey);
        cleanupController(controller);

        // Don't log abort errors (happen during page navigation or timeout)
        const isAbortError =
          error.name === 'AbortError' ||
          error.message === 'Failed to fetch' ||
          error.message?.includes('aborted');

        // Check if this was a timeout
        const isTimeoutError = error.name === 'AbortError' && !controller.signal.reason;

        if (isTimeoutError) {
          console.warn('doApiCall timeout:', endPoint);
          showAlert('Request timed out. Please try again.');
          callback({ status: 'ERROR', error: 'Request timed out', isTimeout: true });
        } else if (!isAbortError) {
          console.error('doApiCall error:', error);
          showAlert(error.message || 'API request failed');
          callback({ status: 'ERROR', error: error.message });
        } else {
          // User-initiated cancellation - silent failure
          callback({ status: 'ERROR', error: 'Request cancelled', isCancelled: true });
        }
      });
  };

  client.doApiCallAsync = function(endPoint, requestData = {}, options = {}) {
    return new Promise((resolve, reject) => {
      client.doApiCall(
        endPoint,
        requestData,
        (data) => {
          if (
            data.status === 'ERROR' ||
            data.status === 'error' ||
            data.error === 'Authentication required'
          ) {
            reject(new Error(data.error || data.errorMsg || 'API request failed'));
          } else {
            resolve(data);
          }
        },
        undefined,
        options
      );
    });
  };

  client.doApiCallGet = function(endPoint, callback, options = {}) {
    const store = getStore();
    const state = store.getState();
    const accessToken = state?.login?.session?.access_token || bearerToken;

    if (!accessToken) {
      callback({ status: 'ERROR', error: 'Authentication required' });
      return;
    }

    const url = consumerConfiguration.serviceProvider.host + endPoint;
    const csrfToken = getCsrfToken();
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      ...buildTenantWorkspaceHeaders(state),
      ...(options.headers || {}),
    };
    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken;
    }

    const timeoutMs = options.timeout || DEFAULT_TIMEOUT_MS;
    const controller = createTimeoutController(timeoutMs);

    const fetchGet = (reqHeaders) =>
      fetch(url, {
        method: 'GET',
        headers: reqHeaders,
        credentials: 'include',
        signal: controller.signal,
      });

    fetchGet(headers)
      .then((response) => {
        if (response.status === 401 && refreshToken && !options.isRetryAfterRefresh) {
          return refreshOAuth2Token()
            .then((newToken) => {
              const retryHeaders = {
                ...headers,
                Authorization: `Bearer ${newToken}`,
              };
              return fetchGet(retryHeaders);
            })
            .catch(() => null);
        }
        return response;
      })
      .then((response) => {
        if (response === null) {
          return { status: 'ERROR', error: 'Authentication expired. Please log in again.' };
        }
        if (!response.ok) {
          return response
            .json()
            .then((data) => ({
              ...data,
              status: 'ERROR',
              error: data.message || data.error || 'Request failed',
              _httpStatus: response.status,
              httpStatus: response.status,
            }))
            .catch(() => ({
              status: 'ERROR',
              error: `Request failed (${response.status})`,
              _httpStatus: response.status,
              httpStatus: response.status,
            }));
        }
        return response.json();
      })
      .then((data) => {
        cleanupController(controller);
        if (isForbiddenApiErrorPayload(data)) {
          notifyForbiddenAction(data);
          callback(data);
          return;
        }
        callback(data);
      })
      .catch((error) => {
        cleanupController(controller);
        // A GET's only abort source is the timeout controller (no dedup/cancel on GETs), so translate an
        // abort into a friendly, flagged result — never leak the raw DOMException text ("signal is aborted
        // without reason") to callers, which would render it verbatim in error states.
        const isAbort = error.name === 'AbortError' || error.message?.includes('aborted');
        if (isAbort) {
          callback({ status: 'ERROR', error: 'Request timed out', isTimeout: true, isCancelled: true });
          return;
        }
        if (error.message !== 'Failed to fetch') {
          console.error('doApiCallGet error:', endPoint, error);
        }
        callback({ status: 'ERROR', error: error.message || 'Request failed' });
      });
  };

  /**
   * OAuth 2.0 API call method - uses Bearer token instead of OAuth 1.0 signing
   * This avoids double-encoding issues and uses modern auth
   *
   * @param {string} endPoint - API endpoint path
   * @param {object} requestData - Request data (will be sent as JSON or form data)
   * @param {function} callback - Callback function
   * @param {object} options - Optional settings { useFormData: boolean, timeout: number }
   */
  client.doApiCallV2 = function(endPoint, requestData, callback, options = {}) {
    const store = getStore();
    const state = store.getState();
    const accessToken = state?.login?.session?.access_token || bearerToken;

    if (!accessToken) {
      console.error('doApiCallV2: No access token available');
      callback({ status: 'ERROR', error: 'Not authenticated' });
      return;
    }

    // Cancel any existing request to this endpoint
    const requestKey = `v2:${endPoint}`;
    if (activeRequests.has(requestKey)) {
      activeRequests.get(requestKey).abort();
    }

    // Create AbortController with timeout
    const timeoutMs = options.timeout || DEFAULT_TIMEOUT_MS;
    const controller = createTimeoutController(timeoutMs);
    activeRequests.set(requestKey, controller);

    const url = consumerConfiguration.serviceProvider.host + endPoint;
    const csrfToken = getCsrfToken();

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      ...buildTenantWorkspaceHeaders(state),
      ...(options.headers || {}),
    };

    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken;
    }

    let body;
    if (options.useFormData) {
      // Send as form-urlencoded (legacy compatibility)
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
      const params = new URLSearchParams();
      for (const key in requestData) {
        if (requestData.hasOwnProperty(key)) {
          params.append(key, requestData[key]);
        }
      }
      body = params.toString();
    } else {
      // Send as JSON (preferred)
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(requestData);
    }

    const fetchOptions = {
      method: 'POST',
      headers,
      credentials: 'include',
      body,
    };

    fetchWithRetry(url, fetchOptions, 0, controller, getMaxRetries(endPoint, options))
      .then((response) => {
        activeRequests.delete(requestKey);

        if (response.status === 401 && refreshToken && !options.isRetryAfterRefresh) {
          return refreshOAuth2Token()
            .then((newToken) => {
              const retryHeaders = {
                ...headers,
                Authorization: `Bearer ${newToken}`,
              };
              const retryOptions = {
                method: 'POST',
                headers: retryHeaders,
                credentials: 'include',
                body,
                signal: controller.signal,
              };
              return fetch(url, retryOptions);
            })
            .then((retryResponse) =>
              retryResponse.json().then((data) => ({
                ...data,
                httpStatus: retryResponse.status,
              }))
            )
            .catch(() => ({
              status: 'ERROR',
              error: 'Authentication expired. Please log in again.',
            }));
        }

        return response.json().then((data) => ({
          ...data,
          httpStatus: response.status,
        }));
      })
      .then((data) => {
        if (isForbiddenApiErrorPayload(data)) {
          notifyForbiddenAction(data);
          callback(data);
          return;
        }
        // Normalize response format for backward compatibility
        if (data.status === 200 || data.status === 'OK' || data.status === 10) {
          // Clear password reset required state on successful password change
          if (endPoint.includes('change-password') || endPoint.includes('changePassword')) {
            store.dispatch({ type: CLEAR_PASSWORD_RESET_REQUIRED });
          }
          callback({
            status: 'OK',
            data: data.data,
            message: data.message,
            ...data,
          });
        } else {
          // Handle password reset required (403 with password_reset_required flag)
          if (data.password_reset_required || data.error === PASSWORD_RESET_REQUIRED) {
            const currentState = store.getState();
            const alreadyFlagged = currentState?.login?.passwordResetRequired;

            store.dispatch({
              type: SET_PASSWORD_RESET_REQUIRED,
              payload: {
                message:
                  data.error_description ||
                  'Your password needs to be updated for security reasons.',
                resetUrl: data.reset_url || '/settings/profile',
              },
            });

            if (typeof window !== 'undefined') {
              const isOnSettingsPage =
                window.location.pathname.includes('/settings') &&
                window.location.search.includes('passwordReset=required');

              // Show notification once (when not already flagged)
              if (!alreadyFlagged) {
                notification.warning({
                  title: 'Password Update Required',
                  description: data.error_description || 'Please update your password to continue.',
                  duration: 0,
                });
              }

              // Only redirect if not already on settings page
              if (!isOnSettingsPage) {
                window.location.href = '/settings/profile?passwordReset=required';
              }
            }
            callback({ status: 'ERROR', error: PASSWORD_RESET_REQUIRED, ...data });
            return;
          }
          // Handle quota exceeded
          if (data.status === QUOTA_EXCEEDED) {
            store.dispatch({
              type: SET_QUOTA_EXCEEDED_INFO,
              quotaExceededInfo: {
                errorMessage: data.message,
              },
            });
          }
          callback(data);
        }
      })
      .catch((error) => {
        activeRequests.delete(requestKey);
        cleanupController(controller);

        const isAbortError =
          error.name === 'AbortError' ||
          error.message === 'Failed to fetch' ||
          error.message?.includes('aborted');

        if (!isAbortError) {
          console.error('doApiCallV2 error:', error);
          showAlert(error.message || 'API request failed');
          callback({ status: 'ERROR', error: error.message });
        } else {
          callback({ status: 'ERROR', error: 'Request cancelled', isCancelled: true });
        }
      });
  };

  /**
   * Public API call method - no authentication required
   * Use this for public endpoints that don't require a Bearer token
   *
   * @param {string} endPoint - API endpoint path
   * @param {object} requestData - Request data
   * @param {function} callback - Callback function
   */
  client.doPublicApiCall = function(endPoint, requestData, callback, method = 'POST') {
    const url = consumerConfiguration.serviceProvider.host + endPoint;
    const csrfToken = getCsrfToken();

    let body;
    const headers = {};
    const isFormData = requestData instanceof FormData;

    if (isFormData) {
      // FormData: let browser set Content-Type with boundary
      body = requestData;
      if (csrfToken) {
        requestData.append('_token', csrfToken);
      }
    } else {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
      const params = new URLSearchParams();
      for (const key in requestData) {
        if (requestData.hasOwnProperty(key)) {
          params.append(key, requestData[key]);
        }
      }
      body = params.toString();
    }

    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken;
    }

    fetch(url, {
      method,
      headers,
      credentials: 'include',
      body,
    })
      .then((response) => response.json())
      .then((data) => {
        // Normalize response
        if (data.status === 200 || data.status === 'OK' || data.status === 10) {
          data.status = 'OK';
        }
        callback(data);
      })
      .catch((error) => {
        console.error('doPublicApiCall error:', error);
        callback({ status: 'ERROR', error: error.message });
      });
  };

  return client;
}

export default arClient();
